import { FieldValidator } from 'final-form';
import { Field, FieldProps } from 'react-final-form';
import styled, { ThemeProvider } from 'styled-components';
import { ErrorContainer, ErrorStyle } from './Error';
import { lgSizeTheme, mdSizeTheme, Size } from './Size';

type Props = {
  label?: string;
  Icon?: () => JSX.Element;
  AdditionalComponent?: () => JSX.Element;
  placeholder?: string;
  errorStyle?: ErrorStyle;
  size?: Size;
  disabled?: boolean;
} & FieldProps<any, any>;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const Label = styled.div<{ hasError: boolean }>`
  font-size: 16px;
  font-weight: 500;
  line-height: 18.75px;
  color: ${props => (props.hasError ? 'rgba(175, 41, 48, 1)' : 'rgba(117, 117, 117, 1)')};
`;

const InputWrapper = styled.div<{ hasError: boolean; disabled: boolean }>`
  position: relative;
  border-radius: 4px;
  background: rgba(245, 245, 245, 1);
  outline: ${props => (props.hasError ? '1px solid rgba(175, 41, 48, 1)' : 'none')};
  padding: 13px 12px 13px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: ${props => (props.disabled ? '0.7' : 1)};
`;

const StyledInput = styled.input<{ hasError?: boolean }>`
  font-size: 18px;
  font-weight: 500;
  line-height: 21px;
  color: ${props => (props.hasError ? '#e93323' : '#000')};
  flex: 1 1 100%;
  background: rgba(245, 245, 245, 1);
  border: none;
  padding: 0;
  padding-right: 30px;
  font-family: Roboto;

  &::placeholder {
    color: ${props => (props.hasError ? '#e93323' : 'initial')};
    font-weight: ${props => (props.hasError ? '500' : '400')};
  }

  &:disabled {
    opacity: 0.7;
  }
`;

const IconWrapper = styled.div`
  display: flex;
`;

export const ContainedTextField = ({
  name,
  label,
  Icon,
  AdditionalComponent,
  placeholder,
  errorStyle = 'default',
  size = 'lg',
  disabled = false,
  ...finalFormProps
}: Props) => {
  return (
    <Field name={name} {...finalFormProps}>
      {fieldProps => {
        const hasError = fieldProps.meta.error && fieldProps.meta.touched;
        const showDefaultError = errorStyle === 'default' && hasError;
        const showInputError = errorStyle === 'filled' && hasError;

        return (
          <Container>
            {label && (
              <Label className="contained_input_label" hasError={showDefaultError}>
                {label}
              </Label>
            )}
            <InputWrapper disabled={disabled} hasError={showDefaultError} className="contained_input_wrapper">
              <StyledInput
                hasError={showInputError}
                {...fieldProps.input}
                disabled={disabled}
                placeholder={placeholder}
                className="contained_input"
              />
              {Icon && (
                <IconWrapper>
                  <Icon />
                </IconWrapper>
              )}
            </InputWrapper>
            {hasError && <ErrorContainer errorStyle={errorStyle}>{fieldProps.meta.error}</ErrorContainer>}
            {AdditionalComponent && <AdditionalComponent />}
          </Container>
        );
      }}
    </Field>
  );
};
